import { useQuery } from '@tanstack/react-query'
import { Input, Select } from 'antd';
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import ViewIcon from '@assets/icons/viewSvg';
import Header from '@components/header'
import Layout from '@components/Layout'
import ViewSalesModal from '@components/Modal/ViewSalesWithDocEntryModal'
import { debouncedSet } from '@utils/debounce'
import { fetchExpectedPayments,fetchDepartments } from '@utils/fetchData'
import { getToday } from '@utils/time'

import { downloadExcel } from '../../utils/downloadPdf';


const ExpectedPayments = () => {
	const { t } = useTranslation()
	const formattedDate = getToday()
	const {roleNames,department : getMeDepartment} = useSelector((state) => state.main)
	const isAdmin = roleNames.includes('admin')

	const [currentPage, setCurrentPage] = useState(0)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)
	const [cardName, setCardName] = useState('')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState(formattedDate)
	const [loadingExcel, setLoadingExcel] = useState(false)
	const [department, setDepartment] = useState(isAdmin ? '' : getMeDepartment)

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'expactedPayment',
			currentPage,
			cardName,
			startDate,
			endDate,
			department
		],
		queryFn: fetchExpectedPayments,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleNextPage = () => {
		if (data.length === 10) {
			setCurrentPage((prevPage) => prevPage + 1)
		}
	}

	const handlePreviousPage = () => {
		if (currentPage > 0) {
			setCurrentPage((prevPage) => prevPage - 1)
		}
	}

	const handleRowClick = (record) => {
		setSelectedRowData(record)
		setIsModalVisible(true)
	}

	const handleStartDate = (e) => {
		setStartDate(e.target.value)
	}

	const handleEndDate = (e) => {
		setEndDate(e.target.value)
	}

	const handleCardNameChange = (val) => {
		debouncedSet(val, setCardName)
	}
	const handleDepartmentChange = (val) => {
		debouncedSet(val, setDepartment)
	}


	const { data: departmentData = [] } = useQuery({
		queryKey: ['departments'],
		queryFn: fetchDepartments,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const columns = [
		{
			title: '',
			children: [
				{
					title: t('numZakaz'),
					dataIndex: 'salesOrderDocNum',
					key: 'salesOrderDocNum',
					text: 'center',
				},
			],
		},
		{
			title: '',
			children: [
				{
					title: t('date'),
					dataIndex: 'docDate',
					key: 'docDate',
					text: 'center',
					render: (text) => {
						const formattedText = moment(text).format('DD.MM.YYYY')
						return `${formattedText}`
					},
				},
			],
		},
		{
			title: (
				<div>
					<Input
						placeholder={t('cardName')}
						onChange={(e) => handleCardNameChange(e.target.value)}
						style={{ width: '100%' }}
					/>
				</div>
			),
			children: [
				{
					title: t('cardName'),
					dataIndex: 'cardName',
					key: 'cardName',
					text: 'center',
				},
			],
		},

		{
			title: '',
			children: [
				{
					title: t('paid'),
					dataIndex: 'dpDocTotal',
					key: 'dpDocTotal',
					text: 'center',
					render: (text, ) => {
						const formattedText = new Intl.NumberFormat(
							'fr-FR',
						).format(text)
						return `${formattedText} UZS`
					},
				},
			],
		},
		{
			title: '',
			children: [
				{
					title: t('debt'),
					dataIndex: 'openAmount',
					key: 'openAmount',
					text: 'center',
					render: (text, ) => {
						const formattedText = new Intl.NumberFormat(
							'fr-FR',
						).format(text)
						return `${formattedText} UZS`
					},
				},
			],
		},
		{
			title: '',
			children: [
				{
					title: t('totalPrice'),
					dataIndex: 'salesOrderDocTotal',
					key: 'salesOrderDocTotal',
					text: 'center',
					render: (text, record) => {
						const formattedText = new Intl.NumberFormat(
							'fr-FR',
						).format(text)
						return `${formattedText} UZS`
					},
				},
			],
		},
		{
			title: (
			isAdmin && 	<div className='min-w-[170px]'>
					<Select
						placeholder={t('department')}
						value={department}
						onChange={(val) => handleDepartmentChange(val)}
						style={{ width: '100%' }}
						showSearch
						filterOption={(input, option) =>
							option.label.toLowerCase().includes(input.toLowerCase())
						}
							options={departmentData.map((department) => {
									return {
										label: department.name,
										value: department.code,
									}
								})}
					/>
				</div>
			),
			// width: 200,
			children: [
				{
					title: t('department'),
					dataIndex: 'departmentName',
					key: 'departmentName',

				},
			],
		},
		{
			title: '',
			children: [
				{
					title: '',
					dataIndex: 'action',
					key: 'action',
					text: 'center',
					render: (text, record) => (
						<div className="flex items-center flex-col gap-2 justify-center">
							<button
								onClick={() => handleRowClick(record)}
								className="bg-[#2198c7] text-white w-[35px] h-[30px] rounded-lg flex justify-center items-center"
							>
								<ViewIcon />
							</button>
						</div>
					),
				},
			],
		},
	]

	const handleDownloadExcel = () => {
		downloadExcel({
			url: 'api/excelgenerators/export-sales-order-open-amounts',
			params: {
				cardName,
				startDate,
				endDate,
				skip: 0,
				pageSize: 10000,
				department,
			},
			method: 'get',
			fileName: t('expectedPayments'),
			onLoadingChange: setLoadingExcel,
		})
	}

	return (
		<Layout>
			<Header
				title={t('expectedPayments')}
				currentPage={currentPage + 1}
				handlePreviousPage={handlePreviousPage}
				handleNextPage={handleNextPage}
				hasMoreData={data.length === 10}
				columns={columns}
				fdata={data}
				loading={isLoading}
				onStartDateChange={handleStartDate}
				onEndDateChange={handleEndDate}
				startDate={startDate}
				endDate={endDate}
				isSticky={false}
					isShowExcelBtn
				handleDownloadExcel={handleDownloadExcel}
				loadingExcel={loadingExcel}
			/>
			<ViewSalesModal
				visible={isModalVisible}
				data={selectedRowData}
				onClose={() => setIsModalVisible(false)}
				ship={roleNames.includes('admin') ? true : false}
				incoming={
					roleNames.includes('admin') || roleNames.includes('Cashier')
						? true
						: false
				}
				isShowStartProduction={
					roleNames.includes('admin') || roleNames.includes('Cashier')
						? true
						: false
				}
			/>
		</Layout>
	)
}

export default ExpectedPayments
